/**
 * /@<handle> — a maker's page: everything live under one handle, on an address of their own.
 *
 * The creator's payoff in the first year is distribution and honest numbers (docs/CONTENT.md,
 * "Numbers for makers"), and distribution needs an address a person can put in a bio and a DM:
 * this one. It is public and it is only what card_public already shows anyone — the handle, when
 * they joined, their live cards, each opening playing at its share link tagged ?via=maker. The
 * host-measured numbers stay the maker's own (/you and /home); nothing here ranks anyone.
 *
 * Mounted by src/index.tsx like /you: no Carousel, no telemetry, no minted session, nothing that
 * emits a row (tests/unit/join.test.ts pins the imports). The two reads are anon-callable RPCs
 * from migration 0006: profile(handle) and cards_by(handle). The build with no backend shows
 * window.__blitzFixture.maker when the e2e suite hands it one, and nobody otherwise.
 */
import { For, Show, createResource, createSignal } from 'solid-js';
import { supabase } from '../backend/supabase';
import { attribution, cardHref, handleFromPath, type HomeRow } from '../lib/home';
import { PageNav } from './PageNav';

export interface MakerProfile {
  handle: string;
  since: string | null;
  card_count: number;
}

interface Fixture {
  maker?: { profile: MakerProfile | null; cards: HomeRow[] };
}

/** The profile and the live cards, or null when there is nobody by that name. */
async function loadMaker(handle: string): Promise<{ profile: MakerProfile; cards: HomeRow[] } | null> {
  if (!supabase) {
    const fx = (window as unknown as { __blitzFixture?: Fixture }).__blitzFixture?.maker;
    return fx?.profile && fx.profile.handle === handle ? { profile: fx.profile, cards: Array.isArray(fx.cards) ? fx.cards : [] } : null;
  }
  const p = await supabase.rpc('profile', { p_handle: handle });
  const profile = p.data as MakerProfile | null;
  if (p.error || !profile || typeof profile.handle !== 'string') return null;
  const c = await supabase.rpc('cards_by', { p_handle: handle });
  return { profile, cards: !c.error && Array.isArray(c.data) ? (c.data as unknown as HomeRow[]) : [] };
}

const cleanAccent = (a: string | null) => (a ? `background:${String(a).replace(/[^#a-z0-9(),.% ]/gi, '')}` : undefined);
const sinceWord = (iso: string | null) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : null;
};

export function MakerPage() {
  const handle = handleFromPath(location.pathname);
  const tag = attribution(location.search, 'maker');
  const [copied, setCopied] = createSignal<string | null>(null);
  const [maker] = createResource(async () => (handle ? loadMaker(handle) : null));

  document.title = handle ? `@${handle} — Blitz` : 'Blitz';

  /** The address itself is the share; the OS sheet where there is one, the clipboard otherwise. */
  const share = async () => {
    const url = `${location.origin}/@${handle}`;
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ url, title: `@${handle} on Blitz` });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied('Link copied.');
    } catch {
      setCopied(url);
    }
  };

  return (
    <div class="app">
      <PageNav current="maker" feed={`/feed${tag}`} feedTestId="maker-feed-top" />

      <main class="home-page maker-page" data-testid="maker-page" data-view={maker.loading ? 'loading' : maker() ? 'maker' : 'none'}>
        <Show when={!maker.loading} fallback={<p class="hint home-status">Reading…</p>}>
          <Show
            when={maker()}
            fallback={
              <section class="home-band" data-testid="maker-none">
                <h1>Nobody here by that name.</h1>
                <p class="hint">Handles are three to twenty letters, digits and underscores. The people who are here are on the <a href="/home">front page</a>.</p>
              </section>
            }
          >
            {(m) => (
              <>
                <section class="home-band maker-head">
                  <span class="ava big" aria-hidden="true">
                    {m().profile.handle[0].toUpperCase()}
                  </span>
                  <h1 data-testid="maker-handle">@{m().profile.handle}</h1>
                  <p class="hint" data-testid="maker-line">
                    {m().cards.length} {m().cards.length === 1 ? 'card' : 'cards'} live
                    <Show when={sinceWord(m().profile.since)}>{(w) => <> · on Blitz since {w()}</>}</Show>
                  </p>
                  <div class="row">
                    <button type="button" class="btn" onClick={() => void share()} data-testid="maker-share">
                      Share this page
                    </button>
                  </div>
                  <Show when={copied()}>
                    <p class="hint" role="status" data-testid="maker-copied">
                      {copied()}
                    </p>
                  </Show>
                </section>
                <Show when={m().cards.length} fallback={<p class="hint home-status">Nothing live yet. What they add waits for one look on a phone first.</p>}>
                  <section class="home-shelf">
                    <ul class="home-grid" data-testid="maker-cards">
                      <For each={m().cards}>
                        {(r) => (
                          <li class="tile">
                            <a href={cardHref(r.card_id, tag)} data-testid="maker-card">
                              <span class="thumb" style={cleanAccent(r.accent)}>
                                <Show when={r.thumbnail_url}>
                                  <img src={r.thumbnail_url!} alt="" loading="lazy" decoding="async" onError={(e) => e.currentTarget.remove()} />
                                </Show>
                              </span>
                              <b>{r.title || r.card_id}</b>
                              <Show when={Number(r.likes) > 0}>
                                <span class="by">♥ {Number(r.likes)}</span>
                              </Show>
                            </a>
                          </li>
                        )}
                      </For>
                    </ul>
                  </section>
                </Show>
              </>
            )}
          </Show>
        </Show>
        <footer class="home-foot">
          <a href="/home">Front page</a> · <a href="/catalogue">Catalogue</a> · <a href="/landing">About Blitz</a>
        </footer>
      </main>
    </div>
  );
}
